import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { OVERVIEW_PERMISSIONS, hasAnyPermission, type OverviewResponse } from '@construction/shared';
import { api } from '@/api/client';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { LanguageSegmented } from '@/components/LanguageSelect';
import { ThemeSelect } from '@/components/ThemeSelect';
import { SiteSwitcher } from '@/components/SiteSwitcher';
import { useLogout } from '@/hooks/useLogout';
import { parseSiteId, pickDefaultSite, useSites } from '@/hooks/useCurrentSite';
import { useMobileShell } from '@/hooks/useMobileShell';
import { usePopoverPlacement } from '@/hooks/usePopoverPlacement';
import { useSiteLock } from '@/hooks/useSiteLock';
import { cn } from '@/lib/cn';
import {
  landingPath,
  matchRoute,
  pathFor,
  routeOpen,
  twinPath,
  visibleEntries,
  type VisibleEntry,
} from '@/lib/routes/logic';
import { ROUTE_LABEL_KEY } from '@/lib/routes/route-labels';
import { useAuthStore } from '@/store/auth';

/**
 * 网页端外壳（CR-082 方案 A，客户在三个方案里选的）。
 *
 * - **外圈和侧栏同一种灰，内容放在一张白色大卡片里**（离四边 8px）：导航和内容一眼分开，又不用一条硬边框。
 * - **菜单分三块**：本工地（概览、图纸、进度表、工地设置）/ 记录（打卡、日报、隐蔽工程、采购、考勤）/ 管理。
 *   客户原话「本工地 记录 管理 三大块，每一块视觉上都需要分割好」：块与块之间一条线 + 组名。
 *   哪一项归哪块写在路由表里（`group`），菜单由路由表按权限生成（CR-040）。
 * - **当前工地**：全局唯一（约束 6），切换后整个菜单都跟着走；在班期间锁住（CR-069）。
 * - **账号区一行**：点开是菜单 —— 修改密码、外观、语言、退出。改之前底部摆着三排控件。
 * - 菜单上的红数：采购 = 待批准 + 待报销，考勤 = 待复核 + 异常（和工地概览同一个接口）。
 *
 * **用哪套外壳由设备决定，不看权限**（客户决定）：窄屏上不渲染这一套，送到 App 样式里对应的那一页。
 */
export function AdminLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobileShell = useMobileShell();
  const location = useLocation();
  const { siteId } = useParams<{ siteId: string }>();
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? null;

  const sites = useSites();
  const list = sites.data ?? [];
  const routeSiteId = parseSiteId(siteId);
  // 在班期间锁在打卡的那个工地（CR-015；网页端能打卡之后同样适用，CR-069）：
  // 不锁的话，网页上打完卡换个工地写日报就绕过去了
  const { lockedSiteId, shift } = useSiteLock();
  const activeSiteId = lockedSiteId ?? routeSiteId ?? pickDefaultSite(list)?.id ?? null;
  const lockedSite = lockedSiteId === null ? null : (list.find((s) => s.id === lockedSiteId) ?? shift?.site ?? null);

  const canOverview = hasAnyPermission(role, OVERVIEW_PERMISSIONS);
  const overview = useQuery({
    queryKey: ['overview', activeSiteId],
    queryFn: () => api.get<OverviewResponse>(`/sites/${activeSiteId}/overview`),
    enabled: activeSiteId !== null && canOverview && !isMobileShell,
    refetchInterval: 60_000,
  });

  /**
   * 换工地时停在同一个子页面上：在「采购」里切工地，看到的还是采购。
   * 当前页不是工地里的页面（工地列表、用户），就进新工地里第一个能看的页面。
   */
  const switchSite = (nextId: number) => {
    const current = matchRoute(location.pathname);
    if (current?.route.siteScoped && routeOpen(current.route, role)) {
      navigate(pathFor(current.route, nextId)!);
      return;
    }
    navigate(landingPath(role, 'desktop', nextId));
  };

  // 手机尺寸下这套导航不该出现，直接让位给手机端外壳
  if (isMobileShell) {
    return (
      <Navigate
        to={twinPath(role, location.pathname, location.search, 'mobile', activeSiteId)}
        replace
      />
    );
  }

  // URL 里是别的工地（手输地址、旧书签）：在班期间一律送回锁定的那个，停在同一个子页面上
  if (lockedSiteId !== null && routeSiteId !== null && routeSiteId !== lockedSiteId) {
    const current = matchRoute(location.pathname);
    const to =
      current?.route.siteScoped && routeOpen(current.route, role)
        ? pathFor(current.route, lockedSiteId)!
        : landingPath(role, 'desktop', lockedSiteId);
    return <Navigate to={to + location.search} replace />;
  }

  const pending = overview.data?.pending ?? null;
  const navCount: Partial<Record<string, number>> = pending
    ? {
        'd.purchases': (pending.purchases ?? 0) + (pending.reimbursements ?? 0),
        'd.attendance': (pending.punches ?? 0) + (pending.anomalies ?? 0),
      }
    : {};

  const current = matchRoute(location.pathname);
  const currentSite = lockedSite ?? list.find((s) => s.id === activeSiteId) ?? null;

  const siteNav = visibleEntries(role, 'siteNav');
  const groups: Array<{ key: string; title: string; items: VisibleEntry[]; siteScoped: boolean }> = [
    { key: 'site', title: t('nav.groupSite'), items: siteNav.filter((e) => e.entry.group === 'site'), siteScoped: true },
    { key: 'records', title: t('nav.groupRecords'), items: siteNav.filter((e) => e.entry.group === 'records'), siteScoped: true },
    { key: 'admin', title: t('nav.system'), items: visibleEntries(role, 'globalNav'), siteScoped: false },
  ].filter((group) => group.items.length > 0 && (!group.siteScoped || activeSiteId !== null));

  return (
    <div className="flex min-h-dvh bg-sidebar">
      <aside className="sticky top-0 flex h-dvh w-[248px] shrink-0 flex-col overflow-y-auto px-2.5 pb-2.5 pt-3">
        {/* 系统名。不放 logo（客户不要图标，CR-081） */}
        <div className="px-2.5 pb-3 pt-1 text-body font-semibold tracking-tight text-ink">{t('app.name')}</div>

        <SiteSwitcher
          id="admin-site"
          sites={list}
          current={currentSite}
          locked={lockedSite !== null}
          onChange={switchSite}
        />

        <nav className="mt-2 flex flex-col">
          {groups.map((group, index) => (
            <div
              key={group.key}
              // 块与块之间：一条线 + 上面留白（客户要「每一块视觉上都需要分割好」）
              className={cn(index > 0 && 'mt-3 border-t border-ink/8 pt-1')}
            >
              <div className="px-2.5 pb-1.5 pt-3 text-micro font-semibold tracking-wide text-ink-3">{group.title}</div>
              <ul className="flex flex-col gap-px">
                {group.items.map(({ route }) => (
                  <li key={route.id}>
                    <NavLink
                      to={group.siteScoped ? pathFor(route, activeSiteId!)! : route.path}
                      // 概览的路径是其它工地页面的前缀，工地列表 /sites 是每个工地页面的前缀：两个都要精确匹配
                      end={route.id === 'd.overview' || !group.siteScoped}
                      data-entry={route.id}
                      className={navClass}
                    >
                      {t(ROUTE_LABEL_KEY[route.id])}
                      {(navCount[route.id] ?? 0) > 0 && (
                        <span className="ml-auto rounded-full bg-danger-solid px-1.5 text-micro font-semibold leading-[18px] tabular-nums text-white">
                          {navCount[route.id]}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <AccountMenu />
      </aside>

      <main className="min-w-0 flex-1 py-2 pr-2">
        <div className="min-h-[calc(100dvh-1rem)] rounded-xl border border-rule bg-surface">
          {/*
            顶栏：在哪个工地的哪一页。贴在顶上，往下滚页面标题没了也知道自己在哪。
          */}
          <div className="sticky top-0 z-nav flex h-[52px] items-center gap-2 rounded-t-xl border-b border-rule bg-surface/90 px-7 text-body backdrop-blur">
            {current?.route.siteScoped && currentSite && (
              <>
                <span className="truncate text-ink-3">{currentSite.name}</span>
                <span aria-hidden className="text-ink-4">
                  /
                </span>
              </>
            )}
            {current && <span className="truncate font-medium text-ink">{t(ROUTE_LABEL_KEY[current.route.id])}</span>}
          </div>
          <div className="max-w-[1360px] px-7 pb-16 pt-6">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * 账号区（CR-082）：一行 —— 名字 / 职位 · 工号，点开是菜单：修改密码、外观、语言、退出。不放头像（客户不要，CR-081）。
 * 菜单往上开（它在屏幕最底下），位置由 `usePopoverPlacement` 按可视区算。
 */
function AccountMenu() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? null;
  const handleLogout = useLogout();
  const account = visibleEntries(role, 'account');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  usePopoverPlacement(open, rootRef, panelRef, { matchWidth: true });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="safe-bottom relative mt-auto border-t border-ink/8 pt-2.5">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('nav.account')}
        data-action="account.menu"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-ink/5',
          open && 'bg-ink/5',
        )}
      >
        <span className="min-w-0">
          <span className="block truncate text-body font-semibold text-ink">{user?.name}</span>
          <span className="block truncate text-caption text-ink-3">
            {role?.name}
            {role && user?.employeeNo ? ' · ' : ''}
            <span className="tabular-nums">{user?.employeeNo}</span>
          </span>
        </span>
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          className="absolute left-0 z-dropdown w-full rounded-xl border border-rule-strong bg-popover p-1.5 shadow-pop"
        >
          {account.map(({ route }) => (
            <NavLink
              key={route.id}
              to={route.path}
              role="menuitem"
              data-entry={route.id}
              onClick={() => setOpen(false)}
              className="flex min-h-touch items-center rounded-md px-2 text-body text-ink hover:bg-ink/6"
            >
              {t(ROUTE_LABEL_KEY[route.id])}
            </NavLink>
          ))}
          <div className="my-1.5 h-px bg-rule" />
          <div className="px-2 py-1.5">
            <div className="mb-1.5 text-caption font-medium text-ink-4">{t('settings.theme')}</div>
            <ThemeSelect hideLabel block />
          </div>
          <div className="px-2 py-1.5">
            <div className="mb-1.5 text-caption font-medium text-ink-4">{t('settings.language')}</div>
            <LanguageSegmented />
          </div>
          <div className="my-1.5 h-px bg-rule" />
          <button
            type="button"
            role="menuitem"
            data-action="logout"
            onClick={() => void handleLogout()}
            className="flex min-h-touch w-full items-center rounded-md px-2 text-left text-body text-danger hover:bg-ink/6"
          >
            {t('auth.logout')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * 菜单项（CR-082 方案 A）：次色字；当前页是一格白色浮起（和内容卡片同一种白），一眼知道「我在这」。
 */
function navClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'relative flex min-h-touch items-center rounded-lg px-2.5 text-body font-medium transition-colors',
    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500',
    isActive
      ? 'bg-surface text-ink shadow-xs ring-1 ring-ink/6'
      : 'text-ink-2 hover:bg-ink/5 hover:text-ink',
  );
}
